import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { isNativeApp } from '../../utils/nativeBridge';
import { isStandalone } from '../../utils/pwa';
import {
  detectMobilePlatform,
  IOS_APP_STORE_URL,
  ANDROID_PLAY_STORE_URL,
} from '../../utils/appStores';

// Monthly cadence: dismissal survives 30 days, then the banner comes back.
// Users on mobile web who have not yet downloaded the app get this nudge
// once every month. Native-wrapper and desktop users never see it.
const DISMISS_KEY = 'akountfly_app_download_dismissed_at';
const DISMISS_DAYS = 30;
const SHOW_DELAY_MS = 8000;

// Feb 2026 — AkountFly is on both stores now. This banner replaces the
// old PWA "Add to Home Screen" prompt and routes to the correct native
// app store based on the visitor's platform. See utils/appStores.js
// for the underlying URLs.
export const InstallAppPrompt = () => {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState(null);
  // Height of the app's own mobile bottom nav (0 on marketing/auth pages).
  // The banner floats above it so the two never overlap.
  const [navOffset, setNavOffset] = useState(0);
  const cleanupRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (!visible) return undefined;
    const measure = () => {
      const nav = document.querySelector('[data-mobile-bottom-nav]');
      setNavOffset(nav ? Math.ceil(nav.getBoundingClientRect().height) : 0);
    };
    // Layouts mount a tick after route change; re-measure a few times.
    measure();
    const t1 = setTimeout(measure, 250);
    const t2 = setTimeout(measure, 1000);
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', measure);
    };
  }, [visible, location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    // Never show inside the native wrapper (they already have the app).
    if (isNativeApp()) return undefined;
    // Never show in an installed PWA context (they already saved it).
    if (isStandalone()) return undefined;
    // Mobile web only.
    if (window.innerWidth > 820) return undefined;
    const p = detectMobilePlatform();
    if (p !== 'ios' && p !== 'android') return undefined;
    setPlatform(p);

    let cancelled = false;

    // Local (fast) gate: if we dismissed within DISMISS_DAYS, bail early.
    try {
      const dismissed = localStorage.getItem(DISMISS_KEY);
      if (dismissed && Date.now() - Number(dismissed) < DISMISS_DAYS * 864e5) {
        return undefined;
      }
    } catch { /* noop */ }

    // Server (authoritative for signed-in users): survives device / browser
    // swaps. Anonymous / logged-out users get a 401 which we treat as
    // "no server state, use local".
    const fetchState = async () => {
      try {
        const url = (process.env.REACT_APP_BACKEND_URL || '') + '/api/users/me/mobile-banner/state';
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) return true; // fall through to local gate
        const j = await res.json();
        return !!j?.should_show;
      } catch {
        return true;
      }
    };

    // Cookie-consent card owns the bottom slot until the visitor resolves
    // it — we wait (no hard cap) so the two never stack on top of each other.
    const isConsentResolved = () => {
      try {
        if (localStorage.getItem('cookieConsent')) return true;
        if (sessionStorage.getItem('cookieConsentDismissed')) return true;
      } catch { /* noop */ }
      return false;
    };
    const waitForConsent = () => new Promise((resolve) => {
      if (isConsentResolved()) return resolve();
      const iv = setInterval(() => {
        if (cancelled || isConsentResolved()) {
          clearInterval(iv);
          resolve();
        }
      }, 500);
    });

    (async () => {
      const eligible = await fetchState();
      if (cancelled || !eligible) return;
      await waitForConsent();
      if (cancelled) return;
      // Small delay so the banner doesn't slap the user on cold-load.
      const t = setTimeout(() => { if (!cancelled) setVisible(true); }, SHOW_DELAY_MS);
      cleanupRef.current = () => clearTimeout(t);
    })();

    return () => {
      cancelled = true;
      if (cleanupRef.current) cleanupRef.current();
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    // Best-effort: sync dismissal to the server so it persists cross-device.
    try {
      const csrf = localStorage.getItem('akountfly_csrf');
      fetch((process.env.REACT_APP_BACKEND_URL || '') + '/api/users/me/mobile-banner/dismiss', {
        method: 'POST',
        credentials: 'include',
        headers: csrf ? { 'X-CSRF-Token': csrf } : {},
      }).catch(() => { /* noop — anon users just ignore */ });
    } catch { /* noop */ }
  };

  const downloadApp = () => {
    const storeUrl = platform === 'ios' ? IOS_APP_STORE_URL : ANDROID_PLAY_STORE_URL;
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    window.location.href = storeUrl;
  };

  if (!visible || !platform) return null;

  const storeLabel = platform === 'ios' ? 'App Store' : 'Google Play';

  return (
    <div
      className="fixed left-4 right-4 z-[60] bg-white rounded-2xl shadow-[0_8px_30px_rgba(40,63,139,0.18)] border border-slate-200 p-4 flex items-center gap-3 md:hidden transition-[bottom] duration-200"
      style={{ bottom: navOffset ? `${navOffset + 12}px` : 'max(1rem, env(safe-area-inset-bottom))' }}
      data-testid="install-app-prompt"
      data-platform={platform}
      data-nav-offset={navOffset}
    >
      <div className="w-10 h-10 rounded-xl bg-[#283F8B]/10 flex items-center justify-center flex-shrink-0">
        <Download className="w-5 h-5 text-[#283F8B]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900">Get the AkountFly app</p>
        <p className="text-xs text-slate-500 truncate">
          Faster access, push alerts &amp; offline receipts on {storeLabel}.
        </p>
      </div>
      <Button
        size="sm"
        className="rounded-full bg-[#283F8B] hover:bg-[#283F8B]/90 flex-shrink-0"
        onClick={downloadApp}
        data-testid="install-app-btn"
      >
        {platform === 'ios' ? 'App Store' : 'Play Store'}
      </Button>
      <button
        onClick={dismiss}
        className="text-slate-400 flex-shrink-0"
        aria-label="Dismiss"
        data-testid="install-dismiss-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default InstallAppPrompt;
